import React from 'react';

import { DateTime } from 'luxon';

import {
  deleteAvailability,
  IAvailabilitiesReports,
  IPharmacyBookingReports,
  IPharmacyWorkshiftReports,
  IReportsData,
  ReportRequestTypes,
  PharmacyWorkshiftStatus,
  TimeFormat,
  PharmacyBookingStatus,
  PharmacyLegendVariant,
  ServiceTypes,
  currency,
  PharmacistLegendVariant,
  ICalendarEventType,
  SupportedTimezones,
  ReportStatus,
  fallbackText,
  convertToDecimals,
  IPharmacyChargesReport,
  ProfessionalReplacementTypes,
  SubscriptionType,
  WorkshiftReportStatus,
} from '@pharmaplan/common';

import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import UpdateTiming from '../../assets/svg/UpdateTiming.svg';
import CancelBookingIcon from '../../assets/svg/newCancelBooking.svg';
import ReportAnIssueIcon from '../../assets/svg/reportAnIssue.svg';
import invoicedIcon from '../../assets/svg/invoicedIcon.svg';

import {
  dateAndTime,
  DateFormats,
  DynamicTableCell,
  MomentTimeFormats,
  PharmacyWorkshiftStatusLabels,
  ReplacementLabels,
  ReplacementTypes,
  statusLabelMap,
  SubscriptionAbbr,
} from '../../helpers/Constants';
import requestBookingIcon from '../../assets/svg/requestBooking.svg';
import submitCounterIcon from '../../assets/svg/submitCounter.svg';
import deleteAvailabilityIcon from '../../assets/svg/delete.svg';
import editAvailability from '../../assets/svg/edit.svg';
import showPassword from '../../assets/svg/showPassword.svg';
import generateInvoice from '../../assets/svg/generateInvoice.svg';
import strings from '../../localization';
import { store } from '../../store';
import { setDialog } from '../../reducers/dialogReducer';

import ReportAnIssue from './ReportAnIssue/ReportAnIssue';
import { formatDateTimeRange } from '../../helpers/Functions';
import Watchlist from '../common/Watchlist';
import MarkAsPaid from '../Invoice/MarkAsPaid';
import { dateRecord, rateRecord } from '../../helpers/longTermRecord';

type IRequestBookingParams = (
  longTermContractId: string,
  id: string,
  isAutoSyncRecord: boolean
) => void;

type ICancelRequestParams = (
  { id, isAutoSyncRecord }: { id?: string; isAutoSyncRecord?: boolean },
  longTermContractId?: string
) => void;

interface IReportsMapper {
  data: Array<IReportsData>;
  type: ReportRequestTypes;
  timeFormat: TimeFormat;
  dispatch: typeof store.dispatch;
  handleRender: HandleRender;
  handleCancellation: HandleCancellation;
  handleRequestCancellation?: ICancelRequestParams;
  requestBooking?: IRequestBookingParams;
  deleteVacation?: (id: string) => void;
  handleInvoice?: (
    bookingId: string,
    isInvoiced: boolean,
    isAutoSyncRecord?: boolean
  ) => void;
  handleMarkAsPaid: (
    longTermShiftContractId?: string
  ) => (bookingId: string) => void;
  markAsPaidIds: Array<string>;
  submitCounterOffer?: (bookingId: string, longTermId?: string) => void;
}

interface IPharmacyReportsMapper {
  data: Array<
    IPharmacyBookingReports | IPharmacyWorkshiftReports | IPharmacyChargesReport
  >;
  type: ReportRequestTypes;
  timeFormat: TimeFormat;
  dispatch: typeof store.dispatch;
  handleRender: HandleRender;
  handleCancellation: HandleCancellation;
  openDrawer: genericAction;
  downloadPaymentReport: (paymentId: string) => () => void;
  deleteShift: (
    isLongTerm: boolean,
    workshiftId: string,
    count?: number
  ) => void;
}

type HandleRender = ({
  id,
  customType,
  isAutoSyncRecord,
}: {
  id: string;
  customType?: ICalendarEventType;
  isAutoSyncRecord?: boolean;
}) => React.JSX.Element | void;

type OpenDrawer = ({
  eventIds,
  type,
  openDetails,
}: {
  eventIds: Array<string>;
  type: ReportRequestTypes;
  openDetails?: boolean;
}) => void;

type preRequestFunc = (workshiftId: string, isAutoSyncRecord?: boolean) => void;
type genericAction = OpenDrawer | preRequestFunc;
const isSelfService = ServiceTypes.self === ServiceHandler.getService();

type HandleCancellation = ({
  id,
  isAutoSyncRecord,
}: {
  id: string;
  isAutoSyncRecord?: boolean;
  longTermShiftContractId?: string;
  professionalId?: string;
}) => void;

export const getHourMinute = (date: string, timeFormat: TimeFormat) =>
  (timeFormat === TimeFormat.twelveHours
    ? DateTime.fromISO(date, { zone: 'utc' }).toFormat(DateFormats.twelveHours)
    : DateTime.fromISO(date, { zone: 'utc' }).toFormat(DateFormats.hourMinutes));

export const formatDate = (
  dateString: string,
  prefix?: string,
  abbreviate = false,
  useEdt = false,
) => {
  const { CANADA } = SupportedTimezones;
  const zone = useEdt ? CANADA : 'utc';
  const currentDate = DateTime.local().setZone(zone);
  const inputDate = DateTime.fromISO(dateString, {
    setZone: true,
    zone,
  });

  const diff = currentDate
    .diff(inputDate, ['days', 'hours', 'minutes'])
    .shiftTo('days', 'hours', 'minutes');
  const { days, hours, minutes } = diff.toObject();
  const roundHours = Math.round(hours ?? 0);
  const roundDays = Math.round(days ?? 0);
  const roundMinutes = Math.round(minutes ?? 0);

  const abbreviations = abbreviate
    ? {
      minutes: strings.minutes.slice(0, 1),
      hours: strings.smallHours.slice(0, 1),
      dayFeedbackMessage: strings.dayFeedbackMessage.slice(0, 1),
      smallDays: strings.smallDays.slice(0, 1),
    }
    : {
      minutes: strings.minutes,
      hours: strings.smallHours,
      dayFeedbackMessage: strings.dayFeedbackMessage,
      smallDays: strings.smallDays,
    };

  const formatRequestedAgo = (value: number, unit: string) =>
    (prefix
      ? strings.formatString(prefix, `${value}`, unit)
      : `${value} ${unit}`);

  switch (true) {
    case roundDays === 0 && (roundHours ?? 0) === 0:
      return formatRequestedAgo(roundMinutes, abbreviations.minutes);
    case roundDays === 0 && (roundHours ?? 0) < 24:
      return formatRequestedAgo(roundHours, abbreviations.hours);
    case roundDays === 1:
      return formatRequestedAgo(roundDays, abbreviations.dayFeedbackMessage);
    case roundDays > 1:
      return formatRequestedAgo(roundDays, abbreviations.smallDays);
    default:
      return '';
  }
};

export const approvalTime = (date: string) =>
  DateTime.fromISO(date).toFormat(`${MomentTimeFormats.dateMonthYear} HH:mm`);

export const getDayMonthDateYear = (date: string) =>
  DateTime.fromISO(date, { setZone: true }).toFormat(
    MomentTimeFormats.weekdayDate,
  );

export const getTodayWithoutOffset = () =>
  DateTime.now().toISO({ includeOffset: false }) ?? '';

export const hasAction = (date: string) =>
  date >= getTodayWithoutOffset();

export const getMinusOneToday = (date: string) =>
  date
  >= (DateTime.now().minus({ days: 1 }).toISO({ includeOffset: false }) ?? '');

export const pharmacistHeaders = () =>
  ({
    [ReportRequestTypes.booked]: [
      { key: '1', label: strings.banner },
      { key: '2', label: strings.pharmacy, sortingKey: 'pharmacyName' },
      { key: '3', label: dateAndTime(), sortingKey: 'startDate' },
      { key: '4', label: strings.rate, sortingKey: 'rate' },
      { key: '5', label: strings.status, sortingKey: 'status' },
      { key: '6', label: strings.action },
    ],
    [ReportRequestTypes.workshift]: [
      { key: '1', label: strings.banner },
      { key: '2', label: strings.city, sortingKey: 'city' },
      { key: '3', label: dateAndTime(), sortingKey: 'startDate' },
      { key: '4', label: strings.rate, sortingKey: 'rate' },
      { key: '5', label: strings.action },
    ],
    [ReportRequestTypes.watchlist]: [
      { key: '1', label: strings.banner },
      { key: '2', label: strings.city, sortingKey: 'city' },
      { key: '3', label: dateAndTime(), sortingKey: 'startDate' },
      { key: '4', label: strings.rate, sortingKey: 'rate' },
      { key: '5', label: strings.action },
    ],
    [ReportRequestTypes.requested]: [
      { key: '1', label: strings.banner },
      { key: '2', label: strings.city, sortingKey: 'city' },
      { key: '3', label: dateAndTime(), sortingKey: 'startDate' },
      { key: '4', label: strings.rate, sortingKey: 'rate' },
      { key: '5', label: strings.status, sortingKey: 'status' },
      { key: '6', label: strings.action },
    ],
    [ReportRequestTypes.availability]: [
      { key: '-1', label: strings.code, sortingKey: 'code' },
      { key: '-2', label: dateAndTime(), sortingKey: 'startDate' },
      { key: '-3', label: strings.action },
    ],
    [ReportRequestTypes.vacation]: [
      { key: '1', label: `${strings.date}`, sortingKey: 'startDate' },
      { key: '2', label: strings.notes },
      { key: '3', label: strings.action, isCenterAligned: true },
    ],
  });

export const pharmacyHeaders = () => {
  let nameObject = [
    {
      key: '2',
      label: strings.pharmacistName,
      sortingKey: 'pharmacistName',
    },
  ];

  let paymentTable = {};

  if (isSelfService) {
    nameObject = [
      {
        key: '2',
        label: strings.professionalName,
        sortingKey: 'professionalName',
      },
      {
        key: '3',
        label: strings.replacementType,
        sortingKey: 'replacementType',
      },
    ];
    paymentTable = {
      [ReportRequestTypes.payment]: [
        { key: '4', label: strings.chargedDate, sortingKey: 'paymentDate' },
        { key: '0', label: strings.plan, sortingKey: 'planName' },
        { key: '2', label: strings.contractDate, sortingKey: 'bookingDate' },
        { key: '3', label: strings.charges, sortingKey: 'amount' },
        { key: '5', label: strings.action },
      ],
    };
  }

  return {
    [ReportRequestTypes.booked]: [
      { key: '1', label: dateAndTime(), sortingKey: 'startDate' },
      ...nameObject,
      { key: '4', label: strings.status, sortingKey: 'bookingStatus' },
      { key: '5', label: strings.action },
    ],
    [ReportRequestTypes.workshift]: [
      { key: '1', label: dateAndTime(), sortingKey: 'startDate' },
      {
        key: '2',
        label: strings.replacementType,
        sortingKey: 'replacementType',
      },
      { key: '3', label: strings.status, sortingKey: 'status' },
      { key: '4', label: strings.action },
    ],
    ...paymentTable,
  };
};

export const handleBookingMapper = ({
  type,
  data,
  timeFormat,
  markAsPaidIds,
  handleMarkAsPaid,
  handleRender,
  handleInvoice,
  handleCancellation,
}: IReportsMapper) => {
  const booked = type === ReportRequestTypes.booked;
  const clickableCriteria = [ReportStatus.Refused, ReportStatus.Canceled];

  return data?.map((item) => {
    const {
      id,
      longTermShiftContractId,
      pharmacyName,
      city,
      startDate,
      endDate,
      status,
      selectedDate,
      logo,
      markAsPaid = false,
      invoiced = false,
      isAutoSyncRecord,
      weekDayPaidHourlyRate = 0,
      rate,
      weekendPaidHourlyRate = 0,
    } = item ?? {};

    const today = DateTime.now().minus({ days: 1 }).toISO({ includeOffset: false }) ?? '';
    const notClickable = !clickableCriteria.includes(status);
    const showIcons = isSelfService;
    const isWithinRange = startDate <= today && today <= endDate;

    const invoiceConfig = invoiced
      ? {
        tooltip: strings.invoiced,
        icon: invoicedIcon,
      }
      : {
        tooltip: strings.generateInvoice,
        icon: generateInvoice,
      };

    const pastActions = showIcons
      ? [
        {
          key: '3',
          tooltip: invoiceConfig.tooltip,
          icon: invoiceConfig.icon,
          disabled: markAsPaidIds.includes(id),
          onClick: () => {
            handleInvoice?.(id, invoiced, isAutoSyncRecord);
          },
        },
        {
          key: '4',
          Component: (
            <MarkAsPaid
              bookingId={id}
              initState={markAsPaid}
              longTermShiftContractId={longTermShiftContractId}
              customFunc={handleMarkAsPaid(longTermShiftContractId)}
              isAutoSyncRecord={isAutoSyncRecord}
            />
          ),
        },
      ]
      : [];

    return {
      key: id,
      data: [
        {
          key: `${id}-2`,
          value: logo,
          type: DynamicTableCell.icon,
        },
        {
          key: `${id}-3`,
          value: `${pharmacyName}\n${city}`,
          type: DynamicTableCell.text,
        },
        dateRecord(
          { startDate, endDate, timeFormat },
          PharmacistLegendVariant.booked,
          longTermShiftContractId,
        ),
        rateRecord(
          { weekDayPaidHourlyRate, weekendPaidHourlyRate, rate },
          longTermShiftContractId,
        ),
        {
          key: `${id}-6`,
          value: statusLabelMap()[status],
          type: DynamicTableCell.status,
        },
        {
          key: `${id}-7`,
          value:
            getMinusOneToday(
              longTermShiftContractId ? endDate : selectedDate,
            ) && notClickable
              ? [
                {
                  key: '1',
                  tooltip: booked
                    ? strings.cancelBooking
                    : strings.cancelRequest,
                  icon: CancelBookingIcon,
                  onClick: () => {
                    handleCancellation({
                      id,
                      isAutoSyncRecord,
                      longTermShiftContractId: booked
                        ? longTermShiftContractId
                        : '',
                    });
                  },
                },
                {
                  key: '2',
                  tooltip: strings.tooltipShowDetails,
                  icon: showPassword,
                  onClick: () => {
                    handleRender({ id, isAutoSyncRecord });
                  },
                },
                ...(isWithinRange ? pastActions : []),
              ]
              : pastActions,
          type: DynamicTableCell.bar,
        },
      ],
    };
  });
};

const handleRequestMapper = ({
  type,
  data,
  timeFormat,
  handleRender,
  handleRequestCancellation,
}: IReportsMapper) => {
  const clickableCriteria = [ReportStatus.Refused, ReportStatus.Canceled];

  return data?.map((item) => {
    const {
      id,
      pharmacyName,
      city,
      startDate,
      endDate,
      status,
      selectedDate,
      logo,
      rate,
      longTermShiftContractId,
      weekDayPaidHourlyRate,
      weekendPaidHourlyRate,
      isAutoSyncRecord,
    } = item ?? {};

    const notClickable = !clickableCriteria.includes(status);
    const isLongTerm = !!longTermShiftContractId;

    return {
      key: id,
      data: [
        {
          key: `${id}-2`,
          value: logo,
          type: DynamicTableCell.icon,
        },
        {
          key: `${id}-3`,
          value:
            type === ReportRequestTypes.booked
              ? `${pharmacyName}\n${city}`
              : city,
          type: DynamicTableCell.text,
        },
        dateRecord(
          { startDate, endDate, timeFormat },
          PharmacistLegendVariant.requested,
          longTermShiftContractId,
        ),
        rateRecord(
          { weekDayPaidHourlyRate, weekendPaidHourlyRate, rate },
          longTermShiftContractId,
        ),
        {
          key: `${id}-6`,
          value: statusLabelMap()[status],
          type: DynamicTableCell.status,
        },
        {
          key: `${id}-7`,
          value:
            hasAction(isLongTerm ? endDate : selectedDate) && notClickable
              ? [
                {
                  key: '1',
                  tooltip: strings.cancelRequest,
                  icon: CancelBookingIcon,
                  onClick: () => {
                    handleRequestCancellation?.(
                      {
                        id: id ?? '',
                        isAutoSyncRecord: !!isAutoSyncRecord,
                      },
                      longTermShiftContractId,
                    );
                  },
                },
                {
                  key: '2',
                  tooltip: strings.tooltipShowDetails,
                  icon: showPassword,
                  onClick: () => {
                    handleRender({ id, isAutoSyncRecord });
                  },
                },
              ]
              : [],
          type: DynamicTableCell.bar,
        },
      ],
    };
  });
};

const handleAvailability = (
  data: IAvailabilitiesReports[],
  timeFormat: TimeFormat,
  dispatch: typeof store.dispatch,
  handleRender: HandleRender,
) =>
  data?.map((item) => {
    const { code, id, startDate, endDate } = item ?? {};

    return {
      key: id,
      data: [
        {
          key: `${id}-1`,
          value: code,
          type: DynamicTableCell.text,
        },
        {
          key: `${id}-2`,
          value: formatDateTimeRange(startDate, endDate, timeFormat),
          type: DynamicTableCell.text,
        },
        {
          key: `${id}-5`,
          value: [
            {
              key: '1',
              tooltip: strings.updateAvailability,
              icon: editAvailability,
              onClick: () => {
                handleRender({ id });
              },
            },
            {
              key: '2',
              tooltip: strings.deleteAvailability,
              icon: deleteAvailabilityIcon,
              onClick: () => {
                dispatch(deleteAvailability({ recurrenceId: '', id }));
              },
            },
          ],
          type: DynamicTableCell.bar,
        },
      ],
    };
  });

const handleWorkshift = (
  data: Array<IReportsData>,
  timeFormat: TimeFormat,
  handleRender: HandleRender,
  requestBooking?: IRequestBookingParams,
  submitCounterOffer?: (bookingId: string, longTermContractId?: string) => void,
) => {
  const onClick = (id: string, isAutoSyncRecord?: boolean) =>
    () => {
      handleRender({ id, isAutoSyncRecord });
    };

  return data?.map((item) => {
    const {
      id,
      city,
      selectedDate,
      logo,
      endDate,
      startDate,
      rate,
      isAutoSyncRecord,
      longTermShiftContractId,
      weekDayPaidHourlyRate,
      weekendPaidHourlyRate,
    } = item ?? {};

    const icons = [
      {
        key: '1',
        tooltip: strings.requestBooking,
        icon: requestBookingIcon,
        onClick: () => {
          requestBooking?.(
            longTermShiftContractId ?? '',
            id,
            !!isAutoSyncRecord,
          );
        },
      },
      {
        key: '2',
        tooltip: strings.tooltipShowDetails,
        icon: showPassword,
        onClick: onClick(id, isAutoSyncRecord),
      },
    ];

    if (isSelfService && !isAutoSyncRecord) {
      icons.splice(1, 0, {
        key: '3',
        tooltip: strings.submitCounterOffer,
        icon: submitCounterIcon,
        onClick: () => {
          submitCounterOffer?.(id, longTermShiftContractId);
        },
      });
    }

    return {
      key: id,
      data: [
        {
          key: `${id}-2`,
          value: logo,
          type: DynamicTableCell.icon,
        },
        {
          key: `${id}-3`,
          value: city,
          type: DynamicTableCell.text,
        },
        dateRecord(
          { startDate, endDate, timeFormat },
          PharmacistLegendVariant.workshift,
          longTermShiftContractId,
        ),
        rateRecord(
          { weekDayPaidHourlyRate, weekendPaidHourlyRate, rate },
          longTermShiftContractId,
        ),
        {
          key: `${id}-6`,
          value: hasAction(selectedDate) ? icons : [],
          type: DynamicTableCell.bar,
        },
      ],
    };
  });
};

const handleWatchlist = (
  data: Array<IReportsData>,
  timeFormat: TimeFormat,
  handleRender: HandleRender,
  requestBooking?: IRequestBookingParams,
) => {
  const onClick = (id: string, isRequested: boolean, isAutoSyncRecord?: boolean) =>
    () => {
      handleRender({
        id,
        customType: isRequested
          ? PharmacistLegendVariant.requested
          : PharmacistLegendVariant.workshift,
        isAutoSyncRecord,
      });
    };

  return data?.map((item) => {
    const {
      id,
      city,
      selectedDate,
      logo,
      endDate,
      startDate,
      rate,
      isRequested,
      isAutoSyncRecord,
      workShiftId,
      longTermShiftContractId,
      weekDayPaidHourlyRate,
      weekendPaidHourlyRate,
    } = item ?? {};

    return {
      key: id,
      data: [
        {
          key: `${id}-2`,
          value: logo,
          type: DynamicTableCell.icon,
        },
        {
          key: `${id}-3`,
          value: city,
          type: DynamicTableCell.text,
        },
        dateRecord(
          { startDate, endDate, timeFormat },
          PharmacistLegendVariant.workshift,
          longTermShiftContractId,
        ),
        rateRecord(
          { weekDayPaidHourlyRate, weekendPaidHourlyRate, rate },
          longTermShiftContractId,
        ),
        {
          key: `${id}-6`,
          value: hasAction(selectedDate)
            ? [
              {
                key: '1',
                Component: (
                  <Watchlist
                    initState
                    workshiftId={workShiftId}
                    showConfirmation
                    isAutoSyncRecord={isAutoSyncRecord}
                  />
                ),
              },
              {
                key: '3',
                tooltip: strings.tooltipShowDetails,
                icon: showPassword,
                onClick: onClick(id, isRequested, isAutoSyncRecord),
              },
              !isRequested
                ? {
                  key: '2',
                  tooltip: strings.requestBooking,
                  icon: requestBookingIcon,
                  onClick: () => {
                    requestBooking?.(
                      longTermShiftContractId ?? '',
                      id,
                      !!isAutoSyncRecord,
                    );
                  },
                }
                : {},
            ]
            : [],
          type: DynamicTableCell.bar,
        },
      ],
    };
  });
};

const handleVacation = (
  data: Array<IReportsData>,
  handleRender: HandleRender,
  deleteVacation?: (id: string) => void,
) =>
  data?.map((item) => {
    const { id, startDate, notes } = item ?? {};

    return {
      key: id,
      data: [
        {
          key: `${id}-1`,
          value: `${getDayMonthDateYear(startDate)}`,
          type: DynamicTableCell.text,
        },
        {
          key: `${id}-2`,
          value: notes,
          type: DynamicTableCell.notes,
          minWidth: 300,
        },
        {
          key: `${id}-6`,
          isCenterAligned: true,
          value: hasAction(startDate)
            ? [
              {
                key: '1',
                show: true,
                tooltip: strings.updateVacation,
                icon: editAvailability,
                onClick: () =>
                  handleRender({ id }),
              },
              {
                key: '2',
                tooltip: strings.deleteVacation,
                icon: deleteAvailabilityIcon,
                onClick: () =>
                  deleteVacation?.(id),
              },
            ]
            : [],
          type: DynamicTableCell.bar,
        },
      ],
    };
  });

const handlePayments = (
  data: Array<IPharmacyChargesReport>,
  downloadPaymentReport: (paymentId: string) => () => void,
) =>
  data?.map((item) => {
    const {
      paymentId,
      paymentDate,
      bookingDate,
      amount,
      startDate,
      endDate,
      longTermShiftContractId,
      professionalId,
      subscriptionType = SubscriptionType.Basic,
    } = item ?? {};
    const chargedDate = paymentDate
      ? getDayMonthDateYear(paymentDate)
      : fallbackText;

    return {
      key: paymentId,
      data: [
        {
          key: `${paymentId}-5`,
          value: chargedDate,
          type: DynamicTableCell.text,
        },
        {
          key: `${paymentId}-1`,
          value: SubscriptionAbbr[subscriptionType],
          type: DynamicTableCell.text,
        },
        dateRecord(
          { startDate: startDate || (bookingDate ?? ''), endDate },
          ReportRequestTypes.payment,
          longTermShiftContractId ?? '',
          professionalId,
        ),
        {
          key: `${paymentId}-4`,
          value: `${currency}${convertToDecimals(amount ?? 0)}`,
          type: DynamicTableCell.text,
        },
        {
          key: '6',
          value: [
            {
              key: '1',
              tooltip: strings.generateInvoice,
              icon: generateInvoice,
              onClick: downloadPaymentReport(paymentId),
            },
          ],
          type: DynamicTableCell.bar,
        },
      ],
    };
  });

export const reportsMapper = ({
  type,
  data,
  timeFormat,
  dispatch,
  handleRender,
  handleCancellation,
  handleInvoice,
  handleMarkAsPaid,
  markAsPaidIds,
  requestBooking,
  handleRequestCancellation,
  deleteVacation,
  submitCounterOffer,
}: IReportsMapper) => {
  switch (type) {
    case ReportRequestTypes.booked:
      return handleBookingMapper({
        data,
        type,
        timeFormat,
        dispatch,
        handleRender,
        handleCancellation,
        handleInvoice,
        handleMarkAsPaid,
        markAsPaidIds,
      });
    case ReportRequestTypes.requested:
      return handleRequestMapper({
        data,
        type,
        timeFormat,
        dispatch,
        handleRender,
        handleCancellation,
        handleInvoice,
        handleRequestCancellation,
        handleMarkAsPaid,
        markAsPaidIds,
      });

    case ReportRequestTypes.availability:
      return handleAvailability(data, timeFormat, dispatch, handleRender);

    case ReportRequestTypes.workshift:
      return handleWorkshift(
        data,
        timeFormat,
        handleRender,
        requestBooking,
        submitCounterOffer,
      );

    case ReportRequestTypes.watchlist:
      return handleWatchlist(data, timeFormat, handleRender, requestBooking);

    case ReportRequestTypes.vacation:
      return handleVacation(data, handleRender, deleteVacation);

    default:
      return [];
  }
};

export const pharmacyReportsMapper = ({
  data,
  dispatch,
  type,
  timeFormat,
  handleCancellation,
  handleRender,
  downloadPaymentReport,
  openDrawer,
  deleteShift,
}: IPharmacyReportsMapper) => {
  switch (type) {
    case ReportRequestTypes.booked:
      return (data as IPharmacyBookingReports[])?.map((item) => {
        const {
          bookingId,
          startDate,
          endDate,
          bookingStatus,
          pharmacistName,
          canReportIssue,
          professionalName,
          longTermShiftContractId,
          professionalId,
          pharmacistId,
          replacementType = ProfessionalReplacementTypes.pharmacist,
        } = item ?? {};

        const isBooked = bookingStatus === PharmacyBookingStatus.booked;
        const name = isSelfService ? professionalName : pharmacistName;
        const id = isSelfService ? professionalId : pharmacistId;
        // eslint-disable-next-line eqeqeq
        const isReplacementTypePresent = replacementType == ProfessionalReplacementTypes.pharmacist
          || replacementType;

        return {
          key: bookingId,
          data: [
            dateRecord(
              { startDate, endDate, timeFormat },
              PharmacistLegendVariant.booked,
              longTermShiftContractId,
              id,
            ),
            {
              key: `${bookingId}-3`,
              value: name,
              type: DynamicTableCell.text,
            },
            isReplacementTypePresent && {
              key: `${bookingId}-4`,
              value:
                ReplacementLabels[
                  replacementType ?? ProfessionalReplacementTypes.pharmacist
                ],
              type: DynamicTableCell.text,
            },
            {
              key: `${bookingId}-5`,
              value: isBooked ? statusLabelMap().booked : null,
              type: DynamicTableCell.status,
            },
            {
              key: `${bookingId}-6`,
              value: hasAction(startDate)
                ? [
                  {
                    key: '1',
                    tooltip: strings.cancelBooking,
                    icon: CancelBookingIcon,
                    onClick: () => {
                      handleCancellation({
                        professionalId,
                        longTermShiftContractId,
                        id: bookingId,
                      });
                    },
                  },
                  canReportIssue && {
                    key: '2',
                    tooltip: strings.reportAnIssue,
                    icon: ReportAnIssueIcon,
                    onClick: () => {
                      dispatch(
                        setDialog({
                          showCloseButton: true,
                          Component: <ReportAnIssue bookingId={bookingId} />,
                          heading: {
                            title: strings.reportAnIssue,
                          },
                          maxWidth: 'sm',
                        }),
                      );
                    },
                  },

                  isSelfService && {
                    key: '3',
                    tooltip: strings.updateTiming,
                    icon: UpdateTiming,
                    onClick: () => {
                      (openDrawer as OpenDrawer)({
                        type,
                        openDetails: true,
                        eventIds: [bookingId],
                      });
                    },
                  },
                ]
                : [],
              type: DynamicTableCell.bar,
            },
          ].filter(Boolean),
        };
      });

    case ReportRequestTypes.workshift:
      return (data as IPharmacyWorkshiftReports[])?.map((item) => {
        const {
          id,
          startDate,
          endDate,
          replacementType,
          type: mappedType,
          longTermShiftContractId,
          totalShiftCount,
          bookedShiftCount,
          status,
        } = item ?? {};

        const shouldShow = hasAction(startDate)
          && PharmacyWorkshiftStatus?.booked !== mappedType;

        return {
          key: id,
          data: [
            dateRecord(
              { startDate, endDate, timeFormat },
              PharmacistLegendVariant.workshift,
              longTermShiftContractId,
            ),
            {
              key: `${id}-4`,
              value: ReplacementTypes()[replacementType],
              type: DynamicTableCell.text,
            },
            {
              key: `${id}-5`,
              value:
                PharmacyWorkshiftStatusLabels()[
                  status ?? WorkshiftReportStatus.posted
                ],
              type: DynamicTableCell.status,
            },
            {
              key: `${id}-6`,
              value: shouldShow
                ? [
                  {
                    key: '1',
                    tooltip: strings.updateWorkshift,
                    icon: editAvailability,
                    onClick: () => {
                      handleRender({
                        id,
                        customType: PharmacyLegendVariant.posted,
                      });
                    },
                  },
                  {
                    key: '2',
                    tooltip: strings.deleteWorkshift,
                    icon: deleteAvailabilityIcon,
                    onClick: () => {
                      deleteShift(
                        !!longTermShiftContractId,
                        id,
                        (totalShiftCount ?? 0) - (bookedShiftCount ?? 0),
                      );
                    },
                  },
                ]
                : [],
              type: DynamicTableCell.bar,
            },
          ],
        };
      });

    case ReportRequestTypes.payment:
      return handlePayments(
        data as Array<IPharmacyChargesReport>,
        downloadPaymentReport,
      );
    default:
      return [];
  }
};

export default reportsMapper;
